/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { memo } from 'react';
import { SidebarBoundary } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/sidebar-boundary/index.tsx';
import { SidebarContent } from '@jira/navigation-apps__sidebar-nav4-sidebars-settings-global-common/src/ui/sidebar-content/index.tsx';
import { Skeleton } from '@jira/navigation-apps__sidebar-nav4-sidebars-settings-global-common/src/ui/skeleton/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyForPaint, type LazyComponent } from '@atlassian/react-loosely-lazy';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useOgTenantInfo } from '@jira/eoc__tenant-info/src/controllers/index.tsx';
import { Redirect, useResource } from '@atlassian/react-resource-router';
import { getConsolidationStateResource } from '@jira/router-resources__eoc-tenant-info/src/getConsolidationStateResource.tsx';
import { fg } from '@atlassian/jira-feature-gating';

export const LazySidebarContent: LazyComponent<React.MemoExoticComponent<() => React.JSX.Element>> =
	lazyForPaint(
		() =>
			import(
				/* webpackChunkName: "async-jira-navigation-apps-sidebar-nav4-sidebar-global-settings-operations-settings" */ './main.tsx'
			),
		{
			includeInShellOnlySsrBundle: true,
			entryFsSsrLiftImportToModule: true,
		},
	);

const AsyncSidebar = (): React.JSX.Element => {
	const isAdmin = useIsAdmin();
	const { isOpsAdmin, shouldUseJSM, loading } = useOgTenantInfo();
	// On shell-only new tab loads (IC), getConsolidationStateResource starts with
	// data=null, loading=false on the first render — before useEffect triggers refresh().
	// All check fns (checkShouldUseJSM etc.) return false for null data, so without this
	// guard the redirect fires before the resource is even fetched.
	// We always call useResource (rules of hooks) but only use it when the gate is on.
	const { data: tenantInfoData } = useResource(getConsolidationStateResource());

	const hasOperationsGeneralConfiguration = isAdmin || isOpsAdmin;

	// Guard: wait for resource data before redirecting — prevents premature redirect
	// when data=null, loading=false on first render (IC shell-only new tab cold start).
	// On shell-only new tab, getConsolidationStateResource starts with data=null, loading=false
	// before useEffect triggers refresh() — all check fns return false for null data, causing
	// a premature redirect to /jira/for-you before ops-user-info is even fetched.
	if ((loading || tenantInfoData === null) && fg('fix_chatops_access_in_isolated_cloud')) {
		return <Skeleton />;
	}

	if (!shouldUseJSM) {
		return <Redirect to="/jira/for-you" />;
	}

	if (!hasOperationsGeneralConfiguration) {
		return (
			<SidebarBoundary
				id="nav4.sidebar.global-settings.operations-settings"
				packageName="jiraNavigationAppsSidebarNav4GlobalSettingsOperationsSettings"
				teamName="alexstrasza"
			>
				<Redirect to="/jira/ops/overview" />
			</SidebarBoundary>
		);
	}

	return (
		<SidebarBoundary
			id="nav4.sidebar.global-settings.operations-settings"
			packageName="jiraNavigationAppsSidebarNav4GlobalSettingsOperationsSettings"
			teamName="alexstrasza"
		>
			<SidebarContent
				currentPageName="operations"
				// eslint-disable-next-line jira/integration/test-id-by-folder-structure
				testId="navigation-apps-sidebar-nav4-sidebars-settings-global-operations.ui.sidebar-content"
			>
				<Placeholder
					name="nav4.sidebar.global-settings.operations-settings"
					fallback={<Skeleton />}
				>
					<LazySidebarContent />
				</Placeholder>
			</SidebarContent>
		</SidebarBoundary>
	);
};

const ExportedComponent: React.MemoExoticComponent<() => React.JSX.Element> = memo(AsyncSidebar);
export default ExportedComponent;
